import { Injectable, inject } from '@angular/core';
import * as Leaflet from 'leaflet';
import {
  MapLayer,
  MapMarker,
  MapMarkerOptions,
  MarkerPopupOptions,
} from '../interfaces';
import { MapOptionsService } from './map-options.service';
import { MarkerType } from '../constants/constants';

@Injectable()
export class MarkersService {
  private mapOptionsService = inject(MapOptionsService);

  markerLayers: MapLayer[] = [];
  markerClusterGroup: Leaflet.MarkerClusterGroup | undefined;

  bindToolTip(
    marker: Leaflet.Marker<MapMarker>,
    markerOptions: MapMarkerOptions,
    markerType?: MarkerType
  ): Leaflet.Marker<MapMarker> {
    return marker.bindTooltip(
      markerOptions.customTooltip
        ? markerOptions.customTooltip
        : (('<div><b>' +
            markerOptions.tooltipValue +
            '</b></div>') as Leaflet.Content),
      {
        direction: markerOptions.tooltipDirection ?? 'top',
        permanent: !!markerOptions.isToolTipPermanent,
        ...this.mapOptionsService.getTooltipClass(markerType),
      }
    );
  }

  bindPopup(marker: MapLayer, popupOptions: MarkerPopupOptions): MapLayer {
    return marker.bindPopup(_ => popupOptions.customElement!, popupOptions);
  }

  getRouteStartAndEndMarkers(
    markerArray: MapMarker[],
    map: Leaflet.Map,
    showRouteMarkers = false
  ): MapLayer[] {
    const len = markerArray.length;
    if (len === 0) return [];

    const result: MapLayer[] = [];

    const startMarker = this.createMarker(markerArray[0], MarkerType.Start);
    startMarker.addTo(map);
    result.push(startMarker);

    // Middle route markers (only if needed)
    if (showRouteMarkers && len > 2) {
      for (let i = 1; i < len - 1; i++) {
        const marker = this.createMarker(
          markerArray[i],
          MarkerType.CustomDefault
        );
        marker.addTo(map);
        result.push(marker);
      }
    }

    // End marker (avoid duplicate if only one point)
    if (len > 1) {
      const endMarker = this.createMarker(markerArray[len - 1], MarkerType.End);
      endMarker.addTo(map);
      result.push(endMarker);
    }

    return result;
  }

  createMarkerClusterPopup(map: Leaflet.Map): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.markerClusterGroup?.on('clusterclick', (a: any) => {
      const markers = a.layer.getAllChildMarkers() as MapMarker[];
      const popupContent = this.createMarkerClusterPopupTable(markers);

      if (!popupContent) return;
      const tableHeadersNo = Object.keys(
        markers[0].options?.popup?.popupTableData?.contentData || {}
      ).length;
      const popup = Leaflet.popup({
        keepInView: true,
        closeButton: true,
        minWidth: tableHeadersNo * 150,
      })
        .setLatLng(a.latlng)
        .setContent(popupContent);

      popup.addTo(map);
    });
  }

  drawCustomMarkers(
    markerArray: MapMarker[],
    map: Leaflet.Map,
    shouldMarkerClusteringBeEnabled: boolean
  ): void {
    for (let index = 0; index < markerArray.length; index++) {
      const markerConfig = markerArray[index];
      const customMarker = this.createMarker(
        markerConfig,
        markerConfig.options?.customMarker?.markerType ||
          MarkerType.CustomDefault
      );

      if (shouldMarkerClusteringBeEnabled) {
        this.markerClusterGroup?.addLayer(customMarker);
      } else {
        customMarker.addTo(map);
        this.markerLayers.push(customMarker);
      }
    }
  }

  drawDefaultMarkers(
    markerArray: MapMarker[],
    map: Leaflet.Map,
    shouldMarkerClusteringBeEnabled: boolean
  ): void {
    for (let index = 0; index < markerArray.length; index++) {
      const markerConfig = markerArray[index];
      const marker = this.createMarker(markerConfig);
      if (shouldMarkerClusteringBeEnabled) {
        this.markerClusterGroup?.addLayer(marker);
      } else {
        marker.addTo(map);
        this.markerLayers.push(marker);
      }
    }
  }

  private createMarker(
    markerConfig: MapMarker,
    markerType?: MarkerType
  ): MapLayer {
    let mapMarker: MapLayer = Leaflet.marker(
      [markerConfig.lat, markerConfig.lng],
      {
        ...markerConfig.options,
        ...this.mapOptionsService.getMarkerIcon(markerType, markerConfig),
        draggable: markerConfig.extraData?.editable,
      }
    );
    if (
      markerConfig.options?.tooltipValue &&
      !markerConfig.extraData?.editable
    ) {
      mapMarker = this.bindToolTip(
        Leaflet.marker([markerConfig.lat, markerConfig.lng], {
          ...markerConfig.options,
          ...this.mapOptionsService.getMarkerIcon(markerType, markerConfig),
        }),
        markerConfig.options,
        markerType
      );
    }
    if (markerConfig.options?.popup) {
      mapMarker = this.bindPopup(mapMarker, markerConfig.options.popup);
    }

    mapMarker.extraData = markerConfig.extraData;

    return mapMarker;
  }

  private createMarkerClusterPopupTable(
    markers: MapMarker[]
  ): string | undefined {
    if (!markers[0].options?.popup?.popupTableData) return;
    const tableHeaders = markers[0].options.popup.popupTableData
      .contentData as { [key: string]: string };

    let tableHeadersBlock = '';
    let contentBlock = '';
    (Object.keys(tableHeaders) as string[]).forEach(header => {
      tableHeadersBlock += `<th style="min-width:100px;padding:5px">${header}</th>`;
    });

    let popupContent =
      '<div class="map-marker-cluster-popup"><table cellspacing="0" class="map-marker-cluster-popup__content">' +
      `<thead><tr>` +
      tableHeadersBlock +
      `</tr></thead>` +
      '<tbody>';

    markers.forEach((marker: MapMarker) => {
      const markerData = marker.options?.popup?.popupTableData;

      let oneMarkerTableValues = '';
      Object.keys(tableHeaders).forEach(header => {
        if (markerData?.iconHeader === header) {
          oneMarkerTableValues += `<td><img src="${markerData.contentData[header]}" style="width:25px;height:25px"></td>`;
        } else {
          oneMarkerTableValues += `<td style="white-space:no-wrap">${markerData?.contentData[header]}</td>`;
        }
      });
      contentBlock += `<tr>${oneMarkerTableValues}</tr>`;
    });

    return (popupContent += contentBlock + '</tbody></table></div>');
  }
}
